/* istanbul ignore file */
import React, { useRef, useEffect } from 'react';
import { useI18n } from '@oneaudi/i18n-context';
import { Text, SearchField, SelectField, SearchFieldProps } from '@oneaudi/unified-web-components';
import { OneCMSContent } from '../../../types/oneCMS';
import { ContentService } from '@oneaudi/content-service';
import {
  initializeAutocomplete,
  useServicesContext,
  setAddressFromLatLngGeocoder,
  getScsUrlPartsFromConfiguration,
  FilterContext,
  initializeInitialStateFromLocalStorage,
  updateLocationSearchInputLocalStorage,
  FilterDTO,
  useApplyFilters,
  useClearAllDealerFilters,
  shouldBeUsedFilterDataWithoutClearingDealers,
  FilterContextValue,
} from '@oneaudi/vtp-shared';
import {
  SearchWrapper,
  LocateMeButton,
  QuickLocationFilterWrapper,
} from '../styles/QuickLocationFilter.styles';
import { ExpiringStorage } from '@oneaudi/stck-store';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { ConfigurationServiceV1 } from '@oneaudi/vtp-configuration-service';
import { SYS_SPACE_FIXED_150, SYS_SPACE_FIXED_300 } from '@oneaudi/unified-web-common';
import { SubHeadText } from '../styles/SwitchNCUC.styles';
import styled from 'styled-components';

export interface QuickLocationFilterProps {}

export const QuickLocationFilter: React.FC<QuickLocationFilterProps> = () => {
  const audiContentService = useServicesContext().getAdditionalService(
    'audi-content-service'
  ) as ContentService;
  const { mileageUnit, locationFilterConfig_radiusOptions, locationFilterConfig_defaultRadius } =
    audiContentService.getContent().fields as OneCMSContent;
  const { getConfiguration, getEnvironmentConfig } = useServicesContext();
  const scsUrlParts = getScsUrlPartsFromConfiguration(getConfiguration()!, getEnvironmentConfig()!);
  const localeService = useServicesContext().getAdditionalService(
    'gfa:locale-service'
  ) as LocaleServiceV1;
  const country = localeService.countryCode;
  const configurationFA = useServicesContext().getAdditionalService(
    'vtp-configuration-service'
  ) as ConfigurationServiceV1;

  const enabledMandatoryAreaSearch =
    configurationFA?.getConfiguration()?.enableMandatoryAreaSearch || false;
  const localStorageLocationSearchInputKey = enabledMandatoryAreaSearch
    ? `VTP_${scsUrlParts?.marketPath}_mandatoryLocationSearch--input`
    : `VTP_${scsUrlParts?.marketPath}_locationSearch--input`;
  const radiusOptions = locationFilterConfig_radiusOptions || [10, 20, 50, 100, 200];
  const defaultRadius = locationFilterConfig_defaultRadius || 10;

  const locationSearchHeadlineLabel = useI18n({
    id: `nemo.sc.plp.quicklocationfilter.locationSearchInputLabel`,
    defaultMessage: 'Location',
  });
  const locationSearchInputLabel = useI18n({
    id: `nemo.sc.plp.quicklocationfilter.locationSearchDescriptionLabel`,
    defaultMessage: 'Enter city or postcode',
  });
  const distanceRadiusLabel = useI18n({
    id: `nemo.sc.plp.electrofiltration.distanceRadiusLabel`,
    defaultMessage: 'Distance',
  });

  const clearLocationSearchInputButtonAriaLabel = useI18n({
    id: `nemo.sc.plp.locationfilter.clearLocationSearchInputButtonAriaLabel`,
    defaultMessage: 'Clear the text in the location search input field.',
  });
  const locateMeButtonAriaLabel = useI18n({
    id: `nemo.sc.plp.locationfilter.cookieConsentRequestMap.locateMeButtonAriaLabel`,
    defaultMessage: 'Use your location.',
  });

  const applyFilters = useApplyFilters();
  const clearAllDealerFilters = useClearAllDealerFilters();

  const localStorage = new ExpiringStorage(localStorageLocationSearchInputKey, 30);
  const inputDataFromLocalStorage = localStorage.readEntry();

  const context = React.useContext(FilterContext) as FilterContextValue;
  const {
    radius,
    setRadius,
    searchedCoords,
    prevSearchedCoords,
    setWholeMarketDealersFetch,
    setSearchedCoords,
    setPrevSearchedCoords,
  } = context;

  const [isClient, setIsClient] = React.useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const prevStateRadius = useRef<any>(null);
  const changedByLocateMe = useRef<boolean>(false);

  const handleLocateMeClick = () => {
    changedByLocateMe.current = true;

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
    };

    const successCallback = (position: GeolocationPosition) => {
      setAddressFromLatLngGeocoder(position, inputRef, localStorageLocationSearchInputKey);

      context.setSearchedCoords({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      });
    };

    const errorCallback = (error: GeolocationPositionError) => {
      // eslint-disable-next-line no-console
      console.error(error);
    };

    navigator.geolocation.getCurrentPosition(successCallback, errorCallback, options);
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (inputRef) {
      const onScroll = () => {
        if (inputRef?.current && inputRef?.current === document.activeElement) {
          inputRef?.current.blur();
        }
      };

      if (typeof window !== 'undefined') {
        document.removeEventListener('scroll', onScroll);
        document.addEventListener('scroll', onScroll, { passive: true });
      }
    }
  }, [inputRef]);

  useEffect(() => {
    if (inputRef.current && window.google) {
      initializeAutocomplete(
        country,
        inputRef,
        localStorageLocationSearchInputKey,
        setSearchedCoords
      );

      if (inputDataFromLocalStorage) {
        setWholeMarketDealersFetch(false);
        initializeInitialStateFromLocalStorage(
          inputRef,
          inputDataFromLocalStorage,
          setRadius,
          setSearchedCoords
        );
      } else {
        setWholeMarketDealersFetch(true);
      }
    }
  }, [inputRef.current]);

  useEffect(() => {
    if (searchedCoords && !radius) {
      setRadius(defaultRadius);
    } else if (searchedCoords && radius && inputRef.current) {
      if (!isClient) return;
      if (!radius) setRadius(defaultRadius);

      const firstAutocompleteSearch = prevStateRadius.current === null;
      const firedByChangedRadius = prevStateRadius.current !== radius;
      const doubleSearchOfSameLocation = prevSearchedCoords?.latitude === searchedCoords.latitude;

      const geoFilterDTO: FilterDTO = {
        id: 'geo',
        active: true,
        type: 'location-filter',
        values: [
          searchedCoords?.latitude as any,
          searchedCoords?.longitude as any,
          radius,
          mileageUnit,
          inputRef.current.value,
        ],
        preset: enabledMandatoryAreaSearch,
      };

      const requestData = async () => {
        // eslint-disable-next-line no-unused-expressions
        shouldBeUsedFilterDataWithoutClearingDealers(firedByChangedRadius, firstAutocompleteSearch)
          ? await applyFilters(geoFilterDTO)
          : await clearAllDealerFilters(geoFilterDTO);
      };

      // eslint-disable-next-line no-console
      if (!doubleSearchOfSameLocation || firedByChangedRadius) requestData().catch(console.error);

      prevStateRadius.current = radius;
      // eslint-disable-next-line no-param-reassign
      changedByLocateMe.current = false;
      setPrevSearchedCoords({
        latitude: searchedCoords?.latitude,
        longitude: searchedCoords?.longitude,
      });
      setWholeMarketDealersFetch(false);
    }
  }, [searchedCoords, radius]);

  useEffect(() => {
    if (radius) {
      updateLocationSearchInputLocalStorage(localStorageLocationSearchInputKey, 'radius', radius);
    }
  }, [radius]);

  useEffect(() => {
    if (inputRef.current?.value && inputRef.current?.value) {
      updateLocationSearchInputLocalStorage(
        localStorageLocationSearchInputKey,
        'inputName',
        inputRef.current.value
      );
    }
  }, [inputRef.current?.value]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = inputDataFromLocalStorage?.inputName || '';
    }
  }, [inputDataFromLocalStorage?.inputName]);

  const autocompleteCSS = `
    .pac-container {
      background-color: #ffffff;
      padding-block: 24px 24px;
      padding-inline: 16px 16px;
      z-index: 10000;
    }

    @media (max-width: 1023px) {
      .pac-container {
        position: fixed !important;
      }
    }

    .pac-icon {
      display: none;
    }
   .pac-item,.pac-item-query, .pac-matched {
      color: '#333333';
      font-size: 16px;
      font-family: AudiType,sans-serif;
      font-stretch: 105%;
      line-height: 24px;
      font-weight: 400;
      border-top: none;
    }
    .pac-item{
      padding: 0px;
    }
     .pac-item:not(:last-child) {
      border-block-end: 1px solid '#d9d9d9';
      padding-block-end: 10px; 
    }
    .pac-item:not(:first-child) {
      padding-block-start: 10px; 
    }
    .pac-item:hover, .pac-item-selected, .pac-item-selected:hover{
      background-color: '#f2f2f2';
      padding-inline-start: 8px;
      cursor: pointer;
    }
    .pac-item:hover span, .pac-item-selected:hover span, .pac-item-selected span{
      color: '#000000';
    }
    .marker-with-counter{
      margin-top: -6px;
    }
  `;

  return (
    <QuickLocationFilterWrapper>
      <SubHeadText
        tag="p"
        variant={{ type: 'copy', order: '2', style: 'short' }}
        margin={[0, 0, SYS_SPACE_FIXED_150, 0]}
      >
        {locationSearchHeadlineLabel}
      </SubHeadText>
      <SearchWrapper>
        <style>{autocompleteCSS}</style>
        <StyledSearchField
          inputRef={inputRef}
          additionalIconButton={{
            onClick: () => {
              if (inputRef?.current) {
                inputRef.current.value = '';
              }
            },
            icon: 'cancel',
            'aria-label': clearLocationSearchInputButtonAriaLabel,
          }}
          // disabled={!consent}
          value={undefined!}
          id="quickLocationFilterInput"
          label={locationSearchHeadlineLabel}
          clearButton={{
            'aria-label': clearLocationSearchInputButtonAriaLabel,
            onClick: () => {},
          }}
        />

        <LocateMeButton
          variant="secondary"
          icon="gps"
          size="small"
          onClick={() => handleLocateMeClick()}
          aria-label={locateMeButtonAriaLabel}
        />
      </SearchWrapper>
      <Text
        variant={{ type: 'copy', style: 'normal', order: '2' }}
        margin={[0, 0, SYS_SPACE_FIXED_300, 0]}
      >
        {locationSearchInputLabel}
      </Text>
      <SelectField
        id="select_radius"
        label={distanceRadiusLabel}
        onChange={(e: any) => {
          setRadius(e.target.value);
        }}
        value={radius!}
        disabled={!radius}
      >
        {radiusOptions.map((radiusOption, index) => {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <option value={radiusOption} key={index}>
              + {radiusOption} {mileageUnit || 'km'}
            </option>
          );
        })}
      </SelectField>
    </QuickLocationFilterWrapper>
  );
};

export default QuickLocationFilter;

export const StyledSearchField = styled((props) => <SearchField {...props} />)<SearchFieldProps>`
  min-width: unset;
`;
